import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * DateInput - Input mascherato per date in formato DD-MM-YYYY
 * 
 * Props:
 * - value: stringa in formato DD-MM-YYYY o YYYY-MM-DD (viene normalizzata internamente)
 * - onChange: callback che riceve il valore in formato DD-MM-YYYY
 * - placeholder: placeholder opzionale (default: "GG-MM-AAAA")
 * - className: classi CSS aggiuntive
 */
const DateInput = React.forwardRef(({ 
  className, 
  value, 
  onChange, 
  placeholder = "GG-MM-AAAA",
  disabled,
  ...props 
}, ref) => {
  const inputRef = React.useRef(null)
  
  // Normalizza il valore in formato DD-MM-YYYY per la visualizzazione
  const normalizeToDisplay = (val) => {
    if (!val) return ""
    // Se è in formato YYYY-MM-DD (dal backend o input type=date), converti in DD-MM-YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const [y, m, d] = val.split("-")
      return `${d}-${m}-${y}`
    }
    return val
  }
  
  // Normalizza il valore in formato YYYY-MM-DD per il backend (opzionale)
  const normalizeToBackend = (val) => {
    if (!val || val.length < 10) return val
    // Se è in formato DD-MM-YYYY, converti in YYYY-MM-DD
    if (/^\d{2}-\d{2}-\d{4}$/.test(val)) {
      const [d, m, y] = val.split("-")
      return `${y}-${m}-${d}`
    }
    return val
  }
  
  const [displayValue, setDisplayValue] = React.useState(() => normalizeToDisplay(value))
  
  // Sincronizza displayValue quando value cambia dall'esterno
  React.useEffect(() => {
    setDisplayValue(normalizeToDisplay(value))
  }, [value])
  
  const handleKeyDown = (e) => {
    const input = inputRef.current
    if (!input) return
    
    const cursorPos = input.selectionStart
    const currentValue = displayValue || ""
    
    // Permetti: Backspace, Delete, Tab, Escape, Enter, frecce
    if ([8, 46, 9, 27, 13, 37, 38, 39, 40].includes(e.keyCode)) {
      if (e.keyCode === 8) { // Backspace
        e.preventDefault()
        
        if (cursorPos === 0) return
        
        let newPos = cursorPos - 1
        // Se siamo dopo un trattino, salta il trattino
        if (currentValue[newPos] === '-') {
          newPos--
        }
        
        if (newPos < 0) return
        
        // Rimuovi il carattere e ricostruisci la stringa
        const chars = currentValue.replace(/-/g, '').split('')
        const charIndex = newPos < 2 ? newPos : (newPos < 5 ? newPos - 1 : newPos - 2)
        
        if (charIndex >= 0 && charIndex < chars.length) {
          chars.splice(charIndex, 1)
          const newValue = formatDateString(chars.join(''))
          setDisplayValue(newValue)
          if (onChange) {
            onChange({ target: { value: normalizeToBackend(newValue) } })
          }
          
          // Posiziona il cursore
          setTimeout(() => {
            const newCursorPos = Math.max(0, cursorPos - 1)
            input.setSelectionRange(newCursorPos, newCursorPos)
          }, 0)
        }
        return
      }
      return
    }
    
    // Permetti Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) {
      return
    }
    
    // Blocca tutto tranne numeri
    if (!/^\d$/.test(e.key)) {
      e.preventDefault()
      return
    }
  }
  
  const formatDateString = (digits) => {
    // Rimuovi tutto tranne i numeri
    const cleaned = digits.replace(/\D/g, '')
    
    let result = ''
    for (let i = 0; i < Math.min(cleaned.length, 8); i++) {
      if (i === 2 || i === 4) {
        result += '-'
      }
      result += cleaned[i]
    }
    
    return result
  }
  
  const handleChange = (e) => {
    const input = e.target
    const rawValue = e.target.value
    const cursorPos = input.selectionStart
    
    // Rimuovi tutto tranne i numeri
    const digitsOnly = rawValue.replace(/\D/g, '')
    
    // Limita a 8 cifre (GGMMAAAA)
    const limitedDigits = digitsOnly.slice(0, 8)
    
    // Formatta con trattini
    const formatted = formatDateString(limitedDigits)
    
    setDisplayValue(formatted)
    
    if (onChange) {
      // Send backend-compatible YYYY-MM-DD format via onChange
      onChange({ target: { value: normalizeToBackend(formatted) } })
    }
    
    // Calcola nuova posizione del cursore
    setTimeout(() => {
      let newPos = cursorPos
      
      // Conta quante cifre ci sono prima della posizione attuale
      const digitsBeforeCursor = rawValue.slice(0, cursorPos).replace(/\D/g, '').length
      
      // Calcola la nuova posizione includendo i trattini
      if (digitsBeforeCursor <= 2) {
        newPos = digitsBeforeCursor
      } else if (digitsBeforeCursor <= 4) {
        newPos = digitsBeforeCursor + 1 // +1 per il primo trattino
      } else {
        newPos = Math.min(digitsBeforeCursor + 2, formatted.length) // +2 per i due trattini
      }
      
      input.setSelectionRange(newPos, newPos)
    }, 0)
  }
  
  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text')
    
    // Estrai solo i numeri dal testo incollato
    const digits = pasted.replace(/\D/g, '').slice(0, 8)
    
    const formatted = formatDateString(digits)
    setDisplayValue(formatted)
    
    if (onChange) {
      onChange({ target: { value: normalizeToBackend(formatted) } })
    }
  }
  
  const handleFocus = (e) => {
    // Seleziona tutto il testo quando l'input riceve focus
    if (props.onFocus) props.onFocus(e)
  }
  
  return (
    <input
      type="text"
      inputMode="numeric"
      ref={(node) => {
        inputRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
      }}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-mono",
        className
      )}
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onFocus={handleFocus}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={10}
      {...props}
    />
  )
})

DateInput.displayName = "DateInput"

export { DateInput }
